import { Component, OnInit, OnDestroy, Input, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  DialogService,
  NoticeService,
  TestListEntity,
  TestListService,
  UserAuth,
  ValuesExchangeProvider,
  TestTypeValue
} from '@nrwl/common-lib';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ITestInfoValue } from '@nrwl/breadcrumb-actions-timepicker';
import { TwampFlowExportService } from './twamp-flow-export.service';

export interface IToolbarInfoData {
  name: string;
  srcAgent: string;
  dstAgent: string;
  testType: string;
  created?: number;
}

export interface IActionItems {
  title: string;
  menuItem: { id: string; option: string; disable: boolean }[];
}

export const ACTION_TOOLBAR = {
  actions_menu: {
    title: 'Actions',
    menuItem: [
      { id: 'start', option: 'Start', disable: false, visible: true, iconName: 'mdi-play-box' },
      { id: 'stop', option: 'Stop', disable: false, visible: true, iconName: 'mdi-stop' },
      {
        id: 'edit',
        option: 'Edit',
        disable: true,
        visible: true,
        iconName: 'mdi-file-document-edit'
      },
      {
        id: 'btf_export',
        option: 'BTF Export',
        disable: false,
        visible: false,
        iconName: 'mdi mdi-export-variant'
      },
      {
        id: 'flow_export',
        option: 'Flow Export',
        disable: false,
        visible: false,
        iconName: 'mdi mdi-export-variant'
      }
    ]
  },
  manageUrl: '/monitor/MonitorManager/v1/monitor'
};

@Component({
  selector: 'test-info-actions',
  templateUrl: './test-info-actions.component.html',
  styleUrls: ['./test-info-actions.component.scss']
})
export class TestInfoActionsComponent implements OnInit, OnDestroy {
  @Input() testInfoValue: ITestInfoValue;
  @Input() testConfig: any;
  @ViewChild('file') file: ElementRef<HTMLInputElement>;
  toolbarData: IToolbarInfoData;
  actionItems: IActionItems;
  testId: string;
  monitorDeleted = false;
  testData: TestListEntity;
  managerUrl: string;
  exportFlowsUrl = '/vibe/inventory/flowexport/flowdefinitions.xlsx?scheduleId=';
  private cacheObj: Subscription;
  private readonly unsubscribe: Subject<void> = new Subject();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialogService: DialogService,
    private readonly noticeService: NoticeService,
    private readonly navigator: NavigationProvider,
    private readonly exchange: ValuesExchangeProvider,
    private readonly userAuth: UserAuth,
    private readonly testListService: TestListService,
    private twampFlowExportService: TwampFlowExportService,
    private elementRef: ElementRef
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  ngOnInit(): void {
    this.actionItems = ACTION_TOOLBAR.actions_menu;
    this.managerUrl = ACTION_TOOLBAR.manageUrl;
    this.exchange.notifyListeners('new_test', { noValue: true });
    this.exchange.registerInterest('new_test', this.updateCache.bind(this));
    this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(queryParams => {
      this.testId = queryParams['testId'];
      this.monitorDeleted = queryParams['monitorDeleted'] === true;
      if (this.testId) {
        this.cacheObj = this.testListService.cache.subscribe(data => {
          this.updatetestData(data);
        });
      }
    });
  }

  ngOnDestroy() {
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  updateCache(data: any): void {
    if (!data.noValue) {
      this.testListService.updateDataSourceTestItem(data);
    }
  }
  updatetestData(tests: TestListEntity[]): void {
    this.testData = this.testListService.findTestCacheByID(tests, this.testId);
    if (this.testData) {
      this.activeTestToolbarDetails();
      this.populateActionItems();
    }
  }

  activeTestToolbarDetails(): void {
    if (this.testData) {
      this.toolbarData = {
        name: this.testData.getAlias(),
        srcAgent: this.testData.getOriginatorAgentAlias(),
        dstAgent: this.testData.getDestinationAgentAlias(),
        testType: this.testData.getTestType()
      };
    }
  }

  populateActionItems(): void {
    this.disableOption(
      'start',
      this.actionItems,
      this.testListService.isTestStarted(this.testData)
    );
    this.disableOption(
      'stop',
      this.actionItems,
      !(
        this.testListService.isTestStarted(this.testData) ||
        this.testListService.isTestAborted(this.testData)
      )
    );
    //Make edit disabled when test started and enabled only when test stopped
    this.disableOption(
      'edit',
      this.actionItems,
      this.testListService.isEditDisable(this.testData) || this.monitorDeleted
    );

    this.checkVisibleOption('btf_export', this.actionItems, this.testData);
    this.checkVisibleOption('flow_export', this.actionItems, this.testData);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  disableOption(optionId: any, actions: any, status: any): any {
    const action = actions.menuItem.find(act => act.id === optionId);
    action.disable = status;
    return actions;
  }

  // eslint-disable-next-line
  checkVisibleOption(optionId: any, actions: any, testData: any): void {
    const action = actions.menuItem.find(act => act.id === optionId);
    action.visible = testData.getTestType() === TestTypeValue.TWAMP;
  }

  isActionApplicable(): boolean {
    return this.userAuth.isAuthedFor('MANAGE_TESTS') ? true : false;
  }
  stopTest(): void {
    this.testListService
      .stopSingleTest$(this.testId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.exchange.notifyListeners('testAction', { stopTest: true, startTest: false });
          const updateResponse = {
            id: response.data?.id,
            status: response.data?.status,
            msg: response.data?.message
          };
          this.testListService.updateTestCacheAfterAction([updateResponse]);
        },
        error => {
          this.noticeService.error(error.error.data.message);
        },
        () => {}
      );
  }

  startTest(): void {
    this.testListService
      .startSingleTest$(this.testId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          const timestamp = new Date().getTime();
          this.exchange.notifyListeners('testAction', {
            stopTest: false,
            startTest: true,
            timestamp
          });
          const updateResponse = {
            id: response.data?.id,
            status: response.data?.status,
            msg: response.data?.message
          };
          this.testListService.updateTestCacheAfterAction([updateResponse]);
        },
        error => {
          this.noticeService.error(error.error.data.message);
        },
        () => {}
      );
  }

  onActionClick(event: string): void {
    switch (event) {
      case 'start': {
        this.startTest();
        break;
      }
      case 'stop': {
        this.confirmStopTest(this.testId);
        break;
      }
      case 'edit': {
        this.testListService.editTest(this.testData);
        break;
      }
      case 'btf_export': {
        this.twampFlowExportService.exportBtfTwampFlows(this.testId);
        break;
      }
      case 'flow_export': {
        this.exportFlowaDefinitions(this.testId);
        break;
      }
    }
  }

  confirmStopTest(testId: string): void {
    const params = {
      id: 'stop.testlist',
      callBackService: this,
      callBackFnParams: testId,
      callBackFuntion: this.stopTest.bind(this)
    };
    this.dialogService.showDialog('confirm', 'body', params);
  }

  exportFlowaDefinitions(scheduleId: string): void {
    const proxyServer = 'https://fusion-ctl-10.ral.ds.jdsu.net';
    const filePath = window.location.hostname.includes('localhost')
      ? proxyServer + this.exportFlowsUrl
      : this.exportFlowsUrl;
    const downloadAnchor = document.createElement('a');
    downloadAnchor.href = filePath + scheduleId;
    downloadAnchor.download = filePath.substring(filePath.lastIndexOf('/') + 1);
    this.elementRef.nativeElement.appendChild(downloadAnchor);
    downloadAnchor.click();
    this.elementRef.nativeElement.removeChild(downloadAnchor);
  }
}
